import { BADGE_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-badge';
import { parseISO } from 'date-fns/parseISO';

import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';
import type { FlightStatusDetailsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightStatusDetailsProps';

type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;
type FlightStatusCode = ArrivalDepartureTypes.FlightStatus;

const statusKeyByStatus: Record<
  string,
  keyof FlightStatusDetailsProps['strings']
> = {
  FLIGHT_STATUS_CANCELLED: 'FlightTrackerFlight_status_value_cancelled',
  FLIGHT_STATUS_DELAYED: 'FlightTrackerFlight_status_value_delayed',
  FLIGHT_STATUS_EXPECTED: 'falcon_adb_flightStatus_status_expected',
  FLIGHT_STATUS_IN_AIR: 'FlightTrackerFlight_status_value_in_air',
  FLIGHT_STATUS_IN_GATE: 'falcon_adb_flightStatus_status_inGate',
  FLIGHT_STATUS_LANDED: 'FlightTrackerFlight_status_value_landed',
  FLIGHT_STATUS_NO_TAKEOFF_INFO: 'FlightTrackerFlight_status_not_available',
  FLIGHT_STATUS_OUT_GATE: 'FlightTrackerFlight_status_value_out_gate',
  FLIGHT_STATUS_PAST_FLIGHT: 'falcon_adb_flightStatus_status_pastFlight',
  FLIGHT_STATUS_SCHEDULED: 'FlightTrackerFlight_status_value_scheduled',
  FLIGHT_STATUS_UNKNOWN: 'FlightTrackerFlight_status_not_available',
  FLIGHT_STATUS_UNSPECIFIED: 'FlightTrackerFlight_status_not_available',
};

const statusBadgeByStatus: Record<string, keyof typeof BADGE_TYPES> = {
  FLIGHT_STATUS_CANCELLED: BADGE_TYPES.critical,
  FLIGHT_STATUS_DELAYED: BADGE_TYPES.critical,
  FLIGHT_STATUS_NO_TAKEOFF_INFO: BADGE_TYPES.warning,
};

export const getStatusLabel = (
  status: FlightStatusUpdate['status'],
  strings: FlightStatusDetailsProps['strings'],
  isDiverted = false,
): string => {
  if (isDiverted) {
    return strings.FlightTrackerFlight_status_value_diverted;
  }

  if (!status) {
    return strings.FlightTrackerFlight_status_not_available;
  }

  const statusKey = statusKeyByStatus[status];

  return statusKey ? strings[statusKey] : status;
};

export const parseFlightDate = (dateTime?: string) => {
  if (!dateTime) {
    return null;
  }

  const parsed = parseISO(dateTime);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const airportTZSuffixPattern = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse an ISO datetime string with `Z` forced on when no TZ suffix is
 * present (CASTLE-4926).
 *
 * `parseISO` interprets a no-TZ string as *local* time, so the Date's
 * UTC parts would depend on the viewer's browser timezone. For airport-
 * local fields this breaks the "hash encodes the airport's wall clock"
 * contract: the hash would shift under the viewer. Forcing `Z` makes the
 * Date's UTC parts carry the airport-local wall clock verbatim, so the
 * hash and the airport-offset derivation are stable across viewers.
 *
 * Inputs that already carry `Z` or a `+HH:MM` offset pass through unchanged.
 */
export const parseAirportLocalOrUTC = (dateTime: string | undefined) => {
  if (!dateTime) return null;
  const normalised = airportTZSuffixPattern.test(dateTime)
    ? dateTime
    : `${dateTime}Z`;
  return parseFlightDate(normalised);
};

export const getStatusBadgeType = (
  status: FlightStatusUpdate['status'],
  isDiverted = false,
) => {
  const statusKey = status || '';

  return (
    (isDiverted ? BADGE_TYPES.critical : statusBadgeByStatus[statusKey]) ||
    BADGE_TYPES.normal
  );
};

export const formatDisplayTime = (
  dateTime: string | undefined,
  locale: string,
  fallbackValue: string | null,
) => {
  const parsed = parseFlightDate(dateTime);

  if (!parsed) {
    return fallbackValue;
  }

  // Localised API fields are provided without timezone suffix.
  // In that case, display the local clock value as-is.
  const hasTimezoneInfo = Boolean(
    dateTime && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(dateTime),
  );

  return parsed.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: hasTimezoneInfo ? 'UTC' : undefined,
  });
};

export const formatDisplayDate = (
  dateTime: string | undefined,
  locale: string,
  fallbackValue: string | null,
) => {
  const parsed = parseFlightDate(dateTime);

  if (!parsed) {
    return fallbackValue;
  }

  // Same timezone handling as formatDisplayTime, so the date shown always
  // matches the calendar day of whichever time is formatted alongside it.
  const hasTimezoneInfo = Boolean(
    dateTime && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(dateTime),
  );

  return parsed.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: hasTimezoneInfo ? 'UTC' : undefined,
  });
};

// Date utility functions shared across components
export const padNumber = (value: number) => String(value).padStart(2, '0');

export const formatDateKey = (date: Date) =>
  `${date.getUTCFullYear()}-${padNumber(date.getUTCMonth() + 1)}-${padNumber(date.getUTCDate())}`;

/**
 * Build a YYYY-MM-DD key from the user's browser-local wall clock
 * (CASTLE-4926 / UAT 10.1). Unlike `formatDateKey`, this uses the
 * non-UTC Date getters so the key follows the viewer's own calendar.
 * Used for the default-selected chip on load and for the "Today" chip
 * label, both of which must track the viewer's today rather than the
 * departure airport's today.
 */
export const formatBrowserLocalDateKey = (date: Date) =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;

export const createDateFromParts = (
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
) => {
  const isoDateTime = `${year.toString().padStart(4, '0')}-${month
    .toString()
    .padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:00.000Z`;
  const candidate = parseISO(isoDateTime);

  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day ||
    candidate.getUTCHours() !== hours ||
    candidate.getUTCMinutes() !== minutes
  ) {
    return null;
  }

  return candidate;
};

export const parseDateKeyToUTCDate = (dateKey: string) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return createDateFromParts(year, month, day, 0, 0);
};

export const shouldUseDivertedStatus = (
  status: FlightStatusCode | undefined,
  isDiverted: boolean,
) => isDiverted && status !== 'FLIGHT_STATUS_CANCELLED';

type DelayFormatStrings = Pick<
  FlightStatusDetailsProps['strings'],
  | 'falcon_adb_flightStatus_delay_hour_plural'
  | 'falcon_adb_flightStatus_delay_hour_singular'
  | 'falcon_adb_flightStatus_delay_hours_and_minutes'
  | 'falcon_adb_flightStatus_delay_minute_plural'
  | 'falcon_adb_flightStatus_delay_minute_singular'
>;

const replaceCount = (template: string, count: string) =>
  template.replaceAll('@@count@@', count);

const placeholderHasAdjacentText = (
  template: string,
  placeholder: '@@hours@@' | '@@minutes@@',
) => {
  const placeholderIndex = template.indexOf(placeholder);

  if (placeholderIndex === -1) {
    return false;
  }

  const beforePlaceholder = template[placeholderIndex - 1] || '';
  const afterPlaceholder =
    template[placeholderIndex + placeholder.length] || '';

  return (
    /[\p{L}\p{N}]/u.test(beforePlaceholder) ||
    /[\p{L}\p{N}]/u.test(afterPlaceholder)
  );
};

/**
 * Formats a delay duration (in minutes) into a human-readable, localised
 * string such as "10 hours and 5 minutes".
 *
 * Rules (see CASTLE-4919):
 * - `delayMinutes < 60` -> "N minute" / "N minutes"
 * - exact multiple of 60 -> "H hour" / "H hours"
 * - otherwise -> "H hour(s) and M minute(s)" via the composable connector
 *   string so the " and " is localisable.
 *
 * Singular/plural is picked via `n === 1`, matching existing Falcon
 * strings-service key-per-plural conventions.
 */
export const formatMinuteDelay = (
  delayMinutes: number,
  locale: string,
  strings: DelayFormatStrings,
) => {
  const numberFormatter = new Intl.NumberFormat(locale);
  const safeMinutes = Math.max(0, Math.trunc(delayMinutes));

  const formatMinutesPart = (minutes: number) => {
    const template =
      minutes === 1
        ? strings.falcon_adb_flightStatus_delay_minute_singular
        : strings.falcon_adb_flightStatus_delay_minute_plural;
    return replaceCount(template, numberFormatter.format(minutes));
  };

  const formatHoursPart = (hours: number) => {
    const template =
      hours === 1
        ? strings.falcon_adb_flightStatus_delay_hour_singular
        : strings.falcon_adb_flightStatus_delay_hour_plural;
    return replaceCount(template, numberFormatter.format(hours));
  };

  if (safeMinutes < 60) {
    return formatMinutesPart(safeMinutes);
  }

  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;

  if (minutes === 0) {
    return formatHoursPart(hours);
  }

  const combinedTemplate =
    strings.falcon_adb_flightStatus_delay_hours_and_minutes;
  const hoursValue = placeholderHasAdjacentText(combinedTemplate, '@@hours@@')
    ? numberFormatter.format(hours)
    : formatHoursPart(hours);
  const minutesValue = placeholderHasAdjacentText(
    combinedTemplate,
    '@@minutes@@',
  )
    ? numberFormatter.format(minutes)
    : formatMinutesPart(minutes);

  return combinedTemplate
    .replaceAll('@@hours@@', hoursValue)
    .replaceAll('@@minutes@@', minutesValue);
};

export type FlightStatusTimeField =
  | 'scheduledDepartureTime'
  | 'estimatedDepartureTime'
  | 'actualDepartureTime'
  | 'scheduledArrivalTime'
  | 'estimatedArrivalTime'
  | 'actualArrivalTime'
  | 'localisedScheduledDepartureTime'
  | 'localisedEstimatedDepartureTime'
  | 'localisedActualDepartureTime'
  | 'localisedScheduledArrivalTime'
  | 'localisedEstimatedArrivalTime'
  | 'localisedActualArrivalTime';

export interface BestTimeResult {
  /** Translated display label text (e.g., "Departs", "Arrives"). */
  label: string;
  /**
   * Formatted time string to display.
   * In getBestDepartureTime/getBestArrivalTime this is returned as display-ready text,
   * including fallback copy when a preferred time value is unavailable, rather than null.
   */
  value: string | null;
  /** True if displaying actual/estimated time that is later than scheduled */
  isDelayed: boolean;
  /** True if displaying actual/estimated time that is earlier than scheduled */
  isEarly: boolean;
  /**
   * Formatted scheduled time for strikethrough display when isDelayed or isEarly is true.
   * Null when showing scheduled time as primary (not comparing against another time).
   */
  scheduledTime: string | null;
}

/**
 * Prefer localised time field if available; fall back to UTC field.
 * Used to handle both new API (with localised*) and legacy API (without).
 */
export const getPreferredTimeField = (
  flight: FlightStatusUpdate,
  localisedField: FlightStatusTimeField,
  utcField: FlightStatusTimeField,
): string | undefined => {
  const localisedValue = (flight as Record<string, unknown>)[localisedField];
  // Only accept the localised value if it actually parses — a malformed
  // field shouldn't block falling back to a valid UTC counterpart.
  if (typeof localisedValue === 'string' && parseFlightDate(localisedValue)) {
    return localisedValue;
  }
  const utcValue = (flight as Record<string, unknown>)[utcField];
  return typeof utcValue === 'string' && parseFlightDate(utcValue)
    ? utcValue
    : undefined;
};

const capitalise = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

export type TimeSource = 'actual' | 'estimated' | 'scheduled' | 'unavailable';

export interface ResolvedTime {
  raw: string | null;
  source: TimeSource;
  formatted: string | null;
}

/**
 * Cascades actual -> estimated -> scheduled (localised preferred over UTC
 * at each step). `source: 'unavailable'` means none resolved. Shared by
 * the subtitle/badge and the departs/arrives table so both agree.
 */
export const resolveTime = (
  flight: FlightStatusUpdate,
  leg: 'departure' | 'arrival',
  locale: string,
  sources: ReadonlyArray<'actual' | 'estimated' | 'scheduled'> = [
    'actual',
    'estimated',
    'scheduled',
  ],
): ResolvedTime => {
  for (const source of sources) {
    const localisedField =
      `localised${capitalise(source)}${capitalise(leg)}Time` as FlightStatusTimeField;
    const utcField = `${source}${capitalise(leg)}Time` as FlightStatusTimeField;
    const raw = getPreferredTimeField(flight, localisedField, utcField);
    if (raw) {
      return { raw, source, formatted: formatDisplayTime(raw, locale, null) };
    }
  }
  return { raw: null, source: 'unavailable', formatted: null };
};

// Scheduled-only resolution — the delay baseline, and used for strings
// that must only ever show the confirmed schedule.
export const getScheduledOnly = (
  flight: FlightStatusUpdate,
  leg: 'departure' | 'arrival',
  locale: string,
): ResolvedTime => resolveTime(flight, leg, locale, ['scheduled']);

export type DelayBucket = 'early' | 'on-time' | 'delayed';

export interface DelayResult {
  /** Signed: negative = early. Only reliable when `isReliable` is true. */
  minutes: number | null;
  bucket: DelayBucket;
  isReliable: boolean;
}

/**
 * Picks a format-matched {scheduledRaw, bestRaw} pair (localised-vs-
 * localised, else UTC-vs-UTC) rather than trusting each side's
 * independently-preferred value, which can mismatch format.
 */
export const getMatchedTimePair = (
  flight: FlightStatusUpdate,
  leg: 'departure' | 'arrival',
  bestSource: 'actual' | 'estimated',
): { scheduledRaw: string; bestRaw: string } | null => {
  const field = (source: string, format: 'localised' | 'utc') => {
    const key = (
      format === 'localised'
        ? `localised${capitalise(source)}${capitalise(leg)}Time`
        : `${source}${capitalise(leg)}Time`
    ) as FlightStatusTimeField;
    const value = (flight as Record<string, unknown>)[key];
    if (typeof value !== 'string' || !parseFlightDate(value)) {
      return null;
    }
    // Must match its slot's own format too.
    const hasSuffix = airportTZSuffixPattern.test(value);
    if (format === 'utc' ? !hasSuffix : hasSuffix) {
      return null;
    }
    return value;
  };

  const localisedScheduled = field('scheduled', 'localised');
  const localisedBest = field(bestSource, 'localised');
  if (localisedScheduled && localisedBest) {
    return { scheduledRaw: localisedScheduled, bestRaw: localisedBest };
  }

  const utcScheduled = field('scheduled', 'utc');
  const utcBest = field(bestSource, 'utc');
  if (utcScheduled && utcBest) {
    return { scheduledRaw: utcScheduled, bestRaw: utcBest };
  }

  return null;
};

/**
 * Any positive delay is reported — no minimum threshold. `best` must be an
 * 'actual'/'estimated' resolution — a 'scheduled'/'unavailable' best means
 * there's nothing to compare the schedule against.
 */
export const getDelay = (
  flight: FlightStatusUpdate,
  leg: 'departure' | 'arrival',
  best: ResolvedTime,
): DelayResult => {
  if (best.source !== 'actual' && best.source !== 'estimated') {
    return { minutes: null, bucket: 'on-time', isReliable: false };
  }
  const pair = getMatchedTimePair(flight, leg, best.source);
  if (!pair) {
    return { minutes: null, bucket: 'on-time', isReliable: false };
  }
  const scheduledDate = parseAirportLocalOrUTC(pair.scheduledRaw);
  const bestDate = parseAirportLocalOrUTC(pair.bestRaw);
  if (!scheduledDate || !bestDate) {
    return { minutes: null, bucket: 'on-time', isReliable: false };
  }
  const minutes = Math.round(
    (bestDate.getTime() - scheduledDate.getTime()) / 60000,
  );
  let bucket: DelayBucket = 'on-time';
  if (minutes <= -1) {
    bucket = 'early';
  } else if (minutes > 0) {
    bucket = 'delayed';
  }
  return { minutes, bucket, isReliable: true };
};

const getBestTime = (
  flight: FlightStatusUpdate,
  leg: 'departure' | 'arrival',
  strings: FlightStatusDetailsProps['strings'],
  locale: string,
): BestTimeResult => {
  const best = resolveTime(flight, leg, locale);
  const scheduledOnly = getScheduledOnly(flight, leg, locale);
  const delay = getDelay(flight, leg, best);

  const label =
    leg === 'departure'
      ? strings.FlightTrackerFlight_other_flights_departs_label
      : strings.FlightTrackerFlight_other_flights_arrives_label;
  const fallback = strings.FlightTrackerFlight_arrival_not_available;

  // Nothing to strike through against unless we're showing an
  // actual/estimated time (vs the schedule itself).
  const isComparable = best.source === 'actual' || best.source === 'estimated';

  return {
    label,
    value: best.formatted ?? fallback,
    isDelayed: isComparable && delay.isReliable && delay.bucket === 'delayed',
    isEarly: isComparable && delay.isReliable && delay.bucket === 'early',
    scheduledTime: isComparable ? scheduledOnly.formatted : null,
  };
};

export const getBestDepartureTime = (
  flight: FlightStatusUpdate,
  strings: FlightStatusDetailsProps['strings'],
  locale: string,
): BestTimeResult => getBestTime(flight, 'departure', strings, locale);

export const getBestArrivalTime = (
  flight: FlightStatusUpdate,
  strings: FlightStatusDetailsProps['strings'],
  locale: string,
): BestTimeResult => getBestTime(flight, 'arrival', strings, locale);
